import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import DealService from '@/api/deal.service';
import CustomerService from '@/api/customers.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { formatDistanceToNow } from 'date-fns';

interface DealDetailFormProps {
  deal: any;
  pipelineStages: any[];
  onSuccess: () => void;
  onDelete?: () => void;
}

export default function DealDetailForm({ deal, pipelineStages, onSuccess, onDelete }: DealDetailFormProps) {
  const [formData, setFormData] = useState({
    title: deal.title || '',
    description: deal.description || '',
    value: deal.value || 0,
    currency: deal.currency || 'USD',
    expectedCloseDate: deal.expectedCloseDate || '',
    dealStageId: deal.dealStageId || '',
    customerId: deal.customerId || '',
    probability: deal.probability || 0
  });
  const [customers, setCustomers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const { toast } = useToast();

  const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
  const businessId = selectedBusiness?.id;

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await CustomerService.getAllCustomers(businessId);
      if (response.data.result === 'success') {
        setCustomers(response.data.customers);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await DealService.updateDeal(deal.id, formData);
      toast({
        title: "Success",
        description: "Deal updated successfully.",
      });
      onSuccess();
    } catch (error) {
      console.error('Error updating deal:', error);
      toast({
        title: "Error",
        description: "Failed to update deal.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    
    setIsLoading(true);
    try {
      await DealService.deleteDeal(deal.id);
      toast({
        title: "Success",
        description: "Deal deleted successfully.",
      });
      onDelete?.();
    } catch (error) {
      console.error('Error deleting deal:', error);
      toast({
        title: "Error",
        description: "Failed to delete deal.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const currentStage = pipelineStages.find(stage => stage.id === deal.dealStageId);
  const selectedCustomer = customers.find(customer => customer.id === deal.customerId);

  return (
    <div className="space-y-6">
      {/* Deal Header Info */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">{deal.title}</h3>
            {currentStage && (
              <Badge variant="secondary">{currentStage.name}</Badge>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            Deal ID: {deal.publicId}
          </div>
          <div className="text-sm text-muted-foreground">
            {deal.dateUpdated 
              ? `Updated ${formatDistanceToNow(new Date(deal.dateUpdated), { addSuffix: true })}`
              : `Created ${formatDistanceToNow(new Date(deal.dateCreated), { addSuffix: true })}`
            }
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold">${deal.value.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">{deal.currency}</div>
        </div>
      </div>

      {/* Customer Info */}
      {selectedCustomer && (
        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
          <Avatar className="w-10 h-10">
            <AvatarImage src={selectedCustomer.profileImg} />
            <AvatarFallback>
              {selectedCustomer.firstName?.[0]}{selectedCustomer.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">
              {selectedCustomer.firstName} {selectedCustomer.lastName}
            </div>
            {selectedCustomer.companyName && (
              <div className="text-sm text-muted-foreground">
                {selectedCustomer.companyName}
              </div>
            )}
            {selectedCustomer.email && (
              <div className="text-sm text-muted-foreground">
                {selectedCustomer.email}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="title">Deal Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="stage">Stage</Label>
            <Select
              value={formData.dealStageId.toString()}
              onValueChange={(value) => setFormData({ ...formData, dealStageId: parseInt(value) })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select stage" />
              </SelectTrigger>
              <SelectContent>
                {pipelineStages.map((stage) => (
                  <SelectItem key={stage.id} value={stage.id.toString()}>
                    {stage.name} ({stage.probability}%)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="value">Deal Value</Label>
            <Input
              id="value"
              type="number"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <Select
              value={formData.currency}
              onValueChange={(value) => setFormData({ ...formData, currency: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="ZAR">ZAR</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="expectedCloseDate">Expected Close Date</Label>
            <Input
              id="expectedCloseDate"
              type="date"
              value={formData.expectedCloseDate}
              onChange={(e) => setFormData({ ...formData, expectedCloseDate: e.target.value })}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="customer">Customer</Label>
            <Select
              value={formData.customerId.toString()}
              onValueChange={(value) => setFormData({ ...formData, customerId: parseInt(value) })}
              disabled={isLoadingCustomers}
            >
              <SelectTrigger>
                <SelectValue placeholder={isLoadingCustomers ? "Loading..." : "Select customer"} />
              </SelectTrigger>
              <SelectContent>
                {customers.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id.toString()}>
                    {customer.firstName} {customer.lastName}
                    {customer.companyName && ` (${customer.companyName})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
          />
        </div>

        <div className="flex justify-between pt-4">
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
            className="gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete Deal
          </Button>
          
          <Button type="submit" disabled={isLoading} className="gap-2">
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
} 